import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';
import { formatRelativeTime, getDateGroup } from '@/lib/date-utils.js';
import { MessageSquare, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { cn } from '@/lib/utils.js';
import type { Session } from '@/types/api.js';
import { LoadingState } from '@/components/common/LoadingState.js';

interface ChatHistoryListProps {
  searchQuery: string;
  onSelect?: () => void;
}

interface GroupedSessions {
  [key: string]: Session[];
}

const GROUP_ORDER = ['Today', 'Yesterday', 'This Week', 'This Month', 'Older'];

export function ChatHistoryList({ searchQuery, onSelect }: ChatHistoryListProps) {
  const { api } = useAuth();
  const { sessionService } = useServices();
  const currentSessionId = useSessionStore(state => state.currentSessionId);
  const addTab = useChatTabsStore((state) => state.addTab);
  const tabs = useChatTabsStore((state) => state.tabs);
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  // Listen for session events from WebSocket (title updates, etc.)
  const lastSessionEvent = useSessionStore((state) => state.lastSessionEvent);

  useEffect(() => {
    if (!lastSessionEvent) return;

    console.log('[ChatHistoryList] Received session event:', lastSessionEvent);

    switch (lastSessionEvent.type) {
      case 'title_updated':
        // Update the session title in local state
        setSessions((prev) =>
          prev.map((session) =>
            session.id === lastSessionEvent.sessionId
              ? { ...session, title: lastSessionEvent.title, lastActivity: Date.now() }
              : session
          ).sort((a, b) => (b.lastActivity || 0) - (a.lastActivity || 0)) // Re-sort after update
        );
        break;

      case 'created':
        // Reload sessions to include the new one
        loadSessions();
        break;

      case 'deleted':
        // Remove from local state
        setSessions((prev) => prev.filter((s) => s.id !== lastSessionEvent.sessionId));
        break;
    }
  }, [lastSessionEvent]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const data = await api.listSessions();
      // Sort by most recent first (using lastActivity)
      setSessions(data.sort((a, b) => (b.lastActivity || 0) - (a.lastActivity || 0)));
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    const isDeletingCurrentSession = sessionId === currentSessionId;

    try {
      console.log('[ChatHistoryList] Deleting session:', {
        sessionId,
        isCurrent: isDeletingCurrentSession,
        currentSessionId
      });

      // Delete from backend
      await api.deleteSession(sessionId);

      // Remove from local state
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));

      // If deleting the current active session, return to welcome
      if (isDeletingCurrentSession) {
        console.log('[ChatHistoryList] Deleted current session, returning to welcome');
        sessionService.returnToWelcome();
        navigate('/');
      }

      console.log('[ChatHistoryList] Session deleted successfully');
    } catch (error) {
      console.error('[ChatHistoryList] Failed to delete session:', error);
      // TODO: Show error toast to user
    }
  };

  // Filter sessions based on search query
  const filteredSessions = sessions.filter((session) => {
    const query = searchQuery.toLowerCase();
    return (
      session.id.toLowerCase().includes(query) ||
      session.title?.toLowerCase().includes(query)
    );
  });

  // Group sessions by date
  const groupedSessions: GroupedSessions = filteredSessions.reduce((groups, session) => {
    const group = getDateGroup(session.lastActivity || Date.now());
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(session);
    return groups;
  }, {} as GroupedSessions);

  if (loading) {
    return <LoadingState message="Loading conversations..." />;
  }

  if (filteredSessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <MessageSquare className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
        <p className="text-sm text-muted-foreground mb-2">
          {searchQuery ? 'No conversations found' : 'No conversations yet'}
        </p>
        <p className="text-xs text-muted-foreground/70">
          {searchQuery ? 'Try a different search term' : "I'm ready when you are. Just ask me anything!"}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin">
      <div className="space-y-6 px-3 py-2 pb-4">
        {GROUP_ORDER.map((groupName) => {
          const groupSessions = groupedSessions[groupName];
          if (!groupSessions || groupSessions.length === 0) return null;

          return (
            <div key={groupName}>
              {/* Group Header */}
              <div className="px-3 py-2 mb-1">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {groupName}
                </h3>
              </div>

              {/* Sessions in this group */}
              <div className="space-y-1">
                {groupSessions.map((session) => (
                  <div
                    key={session.id}
                    onClick={(e) => {
                      console.log('[ChatHistoryList] Session clicked:', session.id);

                      // Check if clicking the delete button
                      if ((e.target as HTMLElement).closest('button')) {
                        console.log('[ChatHistoryList] Click was on button, ignoring');
                        return;
                      }

                      // Check if this session already has a tab open
                      const existingTab = tabs.find((t) => t.sessionId === session.id);

                      if (existingTab) {
                        // Tab exists, just activate it and navigate
                        console.log('[ChatHistoryList] Tab already exists, activating:', existingTab.id);
                        addTab(existingTab); // This will just activate it
                      } else {
                        // Create new tab for this session
                        console.log('[ChatHistoryList] Creating new tab for session:', session.id);
                        addTab({
                          id: `session-${session.id}`,
                          title: session.title || `Chat ${session.id.slice(0, 8)}`,
                          sessionId: session.id,
                          isPinned: false,
                        });
                      }

                      // Navigate immediately - ChatPage will handle session loading
                      // This ensures "Loading conversation..." shows while data loads
                      navigate(`/chat/${session.id}`);
                      onSelect?.();
                    }}
                    className={cn(
                      'group relative px-3 py-2 rounded cursor-pointer transition-all',
                      'hover:bg-muted',
                      // Highlight if this session has an active tab
                      tabs.some((t) => t.sessionId === session.id) && 'bg-primary/10 border border-primary/20'
                    )}
                  >
                    <div className="flex items-center justify-between gap-3">
                      {/* Title */}
                      <p className="font-medium text-sm text-foreground truncate flex-1 min-w-0">
                        {session.title || `Chat ${session.id.slice(0, 8)}`}
                      </p>

                      {/* Timestamp - hidden on hover */}
                      <p className="text-[10px] text-muted-foreground flex-shrink-0 group-hover:hidden">
                        {formatRelativeTime(session.lastActivity || session.createdAt || Date.now())}
                      </p>

                      {/* Delete button - shown on hover */}
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="hidden group-hover:flex transition-opacity flex-shrink-0"
                        onClick={(e) => handleDelete(session.id, e)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
